import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface DirtyFormEntry {
  id: string;
  label: string;
}

interface DirtyFormCtx {
  /** true se almeno un form è attualmente dirty */
  hasDirty: boolean;
  /** Elenco human-readable dei form attualmente dirty */
  dirtyLabels: string[];
  /** Registra un form come dirty (chiamato da useDirtyForm) */
  registerDirty: (id: string, label: string) => void;
  /** Rimuove un form dall'elenco dirty (chiamato da useDirtyForm) */
  clearDirty: (id: string) => void;
  /** Svuota completamente l'elenco (usato dopo un cambio società confermato) */
  resetAll: () => void;
}

const DirtyFormContext = createContext<DirtyFormCtx>({
  hasDirty: false,
  dirtyLabels: [],
  registerDirty: () => {},
  clearDirty: () => {},
  resetAll: () => {},
});

export function DirtyFormProvider({ children }: { children: React.ReactNode }) {
  const [dirtyForms, setDirtyForms] = useState<Map<string, DirtyFormEntry>>(new Map());

  const registerDirty = useCallback((id: string, label: string) => {
    setDirtyForms(prev => {
      // Evita render inutili se già registrato con stessa label
      const existing = prev.get(id);
      if (existing && existing.label === label) return prev;
      const next = new Map(prev);
      next.set(id, { id, label });
      return next;
    });
  }, []);

  const clearDirty = useCallback((id: string) => {
    setDirtyForms(prev => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    setDirtyForms(prev => (prev.size === 0 ? prev : new Map()));
  }, []);

  const value = useMemo<DirtyFormCtx>(() => ({
    hasDirty: dirtyForms.size > 0,
    dirtyLabels: Array.from(dirtyForms.values()).map(e => e.label),
    registerDirty,
    clearDirty,
    resetAll,
  }), [dirtyForms, registerDirty, clearDirty, resetAll]);

  return (
    <DirtyFormContext.Provider value={value}>
      {children}
    </DirtyFormContext.Provider>
  );
}

export function useDirtyFormContext() {
  return useContext(DirtyFormContext);
}
